import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { DeviceType } from '../../shared/enum/EnumRepository';
import CareerSection from './CareerSection';
import createIcon from '../../asset/icon/create.png';
import {
  getDefaultCareerSections,
  resolveCareerSections,
} from './CareerData';
import downloadIcon from '../../asset/icon/download.png';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { bottomWithSafeArea } from '../../shared/constants/safeArea';
import { paperTextureAlert } from '../../shared/component/paperTexture';
import upIcon from '../../asset/icon/up.png';
import belowIcon from '../../asset/icon/below.png';
import reorderIcon from '../../asset/icon/reorder.png';
import { useAboutPage } from '../../shared/hooks/page/useAboutPage';

const CareerList: React.FC = () => {
  const {
    canShowCareer: canShow,
    canReorderCareerSections,
    isEditMode,
    careerList,
    careerSectionOrder,
    deviceType,
    setting,
    handleCreateElement,
    handleCreateCareer,
    handleDownloadPDFInMobile,
    handleReorderCareerSections,
  } = useAboutPage();
  const [showAllCareerTypes, setShowAllCareerTypes] = useState(false);
  const orderedCareerSections = resolveCareerSections(careerSectionOrder);
  const defaultCareerSections = getDefaultCareerSections(
    setting,
    orderedCareerSections,
  );
  const defaultCareerTypes = new Set(
    defaultCareerSections.map((section) => section.type),
  );
  const additionalCareerSections = orderedCareerSections.filter(
    (section) => !defaultCareerTypes.has(section.type),
  );

  useEffect(() => {
    setShowAllCareerTypes(false);
  }, [setting?.aui]);

  const renderCreateButton = () => {
    if (deviceType === DeviceType.MOBILE) {
      return (
        <CreateButtonGroupMobile>
          <MobileToolbarSection>
            <UniversalIconBtn
              size="standard"
              border={false}
              isTransparent
              backgroundImg={createIcon}
              onClick={handleCreateCareer}
            />
          </MobileToolbarSection>
          <MobileToolbarSection>
            <UniversalIconBtn
              size="standard"
              border={false}
              isTransparent
              backgroundImg={downloadIcon}
              onClick={handleDownloadPDFInMobile}
            />
          </MobileToolbarSection>
          {canReorderCareerSections && (
            <MobileToolbarSection>
              <UniversalIconBtn
                size="standard"
                border={false}
                isTransparent
                backgroundImg={reorderIcon}
                onClick={handleReorderCareerSections}
                aria-label="Reorder career sections"
              />
            </MobileToolbarSection>
          )}
        </CreateButtonGroupMobile>
      );
    } else {
      return (
        <CreateButtonGroup>
          {defaultCareerSections.map((section) => (
            <BtnSection
              key={section.type}
              onClick={() => handleCreateElement(section.type, section.storageRange)}>
              {section.title}
            </BtnSection>
          ))}
          {additionalCareerSections.map((section) => (
            <ExpandableCell
              key={section.type}
              $isExpanded={showAllCareerTypes}
              aria-hidden={!showAllCareerTypes}
            >
              <ExpandableCellInner>
                <BtnSection
                  onClick={() => handleCreateElement(section.type, section.storageRange)}
                >
                  {section.title}
                </BtnSection>
              </ExpandableCellInner>
            </ExpandableCell>
          ))}
          {defaultCareerSections.length < orderedCareerSections.length && (
            <CareerTypeToggle
              type="button"
              aria-label={showAllCareerTypes ? 'Collapse career types' : 'Show all career types'}
              aria-expanded={showAllCareerTypes}
              onClick={() => setShowAllCareerTypes((current) => !current)}
            >
              <CareerTypeToggleIcon
                $backgroundImg={showAllCareerTypes ? upIcon : belowIcon}
              />
            </CareerTypeToggle>
          )}
        </CreateButtonGroup>
      );
    }
  }

  return (
    <CareerListComp>
      {canShow && isEditMode && careerList.length === 0 &&
        <EmptyCareerListContainer>
          {getPageMessage('NO_CAREER_FOUND')}
        </EmptyCareerListContainer>
      }
      {canShow && orderedCareerSections.map((section) => (
        <CareerSection
          key={section.title}
          type={section.type}
          title={section.title}
          storageRange={section.storageRange}
          displayRange={section.displayRange}
        />
      ))}
      {isEditMode && renderCreateButton()}
    </CareerListComp>
  );
}

const EmptyCareerListContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const CareerListComp = styled.section`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  padding-bottom: clamp(20px, 4vw, 40px);
`;

const CreateButtonGroup = styled.div`
  position: fixed;
  bottom: clamp(70px, 6vw, 100px);
  right: clamp(20px, 2vw, 30px);
  display: grid;
  grid-template-columns: max-content;
  width: fit-content;
  max-width: calc(100vw - 12vw);
  justify-content: end;
  align-content: center;
  gap: 0;
  box-sizing: border-box;
  ${paperTextureAlert}
`;

/** 모바일: 한 장의 종이 질감 안에 액션을 가로로 두 섹션으로 나누고, 사이에 세로 구분선 */
const CreateButtonGroupMobile = styled.div`
  position: fixed;
  bottom: ${bottomWithSafeArea('clamp(30px, 7vw, 40px)')};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: fit-content;
  max-width: calc(100vw - 12vw);
  box-sizing: border-box;
  ${paperTextureAlert}
`;

const MobileToolbarSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 5%;
    bottom: 2%;
    width: 1px;
    background: linear-gradient(
      to bottom,
      ${({ theme }) => theme.colors.color_Gray_06} 0%,
      ${({ theme }) => theme.colors.color_Gray_05} 60%,
      ${({ theme }) => theme.colors.color_Gray_05} 80%,
      ${({ theme }) => theme.colors.color_Gray_06} 100%
    );
  }
`;

const BtnSection = styled.div`
  position: relative;
  padding: 8px 10px;
  ${({ theme }) => theme.typography.Body_04};
  &::after {
    content: '';
    position: absolute;
    left: 5%;
    right: 5%;
    bottom: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      ${({ theme }) => theme.colors.color_Gray_06} 10%,
      ${({ theme }) => theme.colors.color_Gray_06} 95%,
      transparent
    );
  }
  &:hover {
    transform: scale(0.95);
  }
  transition: all 0.3s ease-in-out;
  cursor: pointer;
`;

const ExpandableCell = styled.div<{ $isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? '1fr' : '0fr')};
  min-width: 0;
  min-height: 0;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  visibility: ${({ $isExpanded }) => ($isExpanded ? 'visible' : 'hidden')};
  pointer-events: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'none')};
  transition:
    grid-template-rows 0.45s ease-in-out,
    opacity 0.3s ease-in-out,
    visibility 0s ${({ $isExpanded }) => ($isExpanded ? '0s' : '0.45s')};
`;

const ExpandableCellInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const CareerTypeToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  cursor: pointer;
`;

const CareerTypeToggleIcon = styled.span<{ $backgroundImg: string }>`
  display: block;
  width: 8px;
  height: 8px;
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
`;

export default CareerList;
